import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import "./OrganizerDashboard.css";

interface Mariage {
  _id: string;
  bride: string;
  groom: string;
  date: string;
}

interface Stats {
  mariage: { bride: string; groom: string; date: string };
  totals: { totalInvites: number; confirmedCount: number; pendingCount: number; taux: string };
  boissons: Array<{ _id: string; count: number }>;
}

interface Invite {
  _id: string;
  nom: string;
  table: string;
  couple: string;
  statut: string;
  boissonPreferee: string;
  telephone: string;
}

interface MariagesResponse {
  success: boolean;
  mariages: Mariage[];
}

interface StatsResponse {
  success: boolean;
  data: Stats;
}

interface InvitesResponse {
  success: boolean;
  invites: Invite[];
}

const OrganizerDashboard: React.FC = () => {
  const { logout } = useAuth();
  const [mariages, setMariages] = useState<Mariage[]>([]);
  const [selectedMariage, setSelectedMariage] = useState<string>("");
  const [stats, setStats] = useState<Stats | null>(null);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [filter, setFilter] = useState({ statut: "all", search: "" });
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<string>("");
  
  const scrollPositionRef = useRef<number>(0);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const saveScrollPosition = () => {
    if (tableContainerRef.current) {
      scrollPositionRef.current = tableContainerRef.current.scrollTop;
    }
  };

  const restoreScrollPosition = () => {
    if (tableContainerRef.current && scrollPositionRef.current > 0) {
      tableContainerRef.current.scrollTop = scrollPositionRef.current;
    }
  };

  useEffect(() => {
    const fetchMariages = async () => {
      try {
        const res = await api.get<MariagesResponse>("/mariages");
        setMariages(res.data.mariages);
        if (res.data.mariages.length > 0) {
          setSelectedMariage(res.data.mariages[0]._id);
        }
      } catch (error) {
        console.error("Erreur chargement mariages:", error);
      }
    };
    fetchMariages();
  }, []);

  const refreshData = useCallback(async () => {
    if (!selectedMariage) return;
    
    try {
      const statsRes = await api.get<StatsResponse>(`/organizer/${selectedMariage}/stats`);
      setStats(statsRes.data.data);
      
      const params = new URLSearchParams();
      if (filter.statut !== "all") params.append("statut", filter.statut);
      if (filter.search) params.append("search", filter.search);
      
      const invitesRes = await api.get<InvitesResponse>(`/organizer/${selectedMariage}/invites?${params.toString()}`);
      setInvites(invitesRes.data.invites);
      setLastUpdate(new Date().toLocaleTimeString());
      
      restoreScrollPosition();
    } catch (error) {
      console.error("Erreur rafraîchissement:", error);
    }
  }, [selectedMariage, filter]);

  const loadData = useCallback(async () => {
    if (!selectedMariage) return;
    
    saveScrollPosition();
    setLoading(true);
    try {
      const statsRes = await api.get<StatsResponse>(`/organizer/${selectedMariage}/stats`);
      setStats(statsRes.data.data);
      
      const params = new URLSearchParams();
      if (filter.statut !== "all") params.append("statut", filter.statut);
      if (filter.search) params.append("search", filter.search);
      
      const invitesRes = await api.get<InvitesResponse>(`/organizer/${selectedMariage}/invites?${params.toString()}`);
      setInvites(invitesRes.data.invites);
      setLastUpdate(new Date().toLocaleTimeString());
      
      setTimeout(() => restoreScrollPosition(), 100);
    } catch (error) {
      console.error("Erreur chargement données:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedMariage, filter]);

  useEffect(() => {
    if (selectedMariage) {
      loadData();
      const interval = setInterval(() => {
        refreshData();
      }, 10000); 
      return () => clearInterval(interval);
    }
  }, [selectedMariage, loadData, refreshData]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('scroll', saveScrollPosition);
      return () => container.removeEventListener('scroll', saveScrollPosition);
    }
  }, []);

  const handleLogout = () => {
    logout();
    window.location.href = "/";
  };

  return (
    <div className="organizer-dashboard">
      <div className="dashboard-header">
        <h1>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" fill="currentColor"/>
          </svg>
          Tableau de bord organisateur
        </h1>
        <div className="header-info">
          <select 
            value={selectedMariage} 
            onChange={(e) => setSelectedMariage(e.target.value)}
            className="mariage-select"
          >
            {mariages.map(m => (
              <option key={m._id} value={m._id}>
                {m.bride} & {m.groom} - {m.date}
              </option>
            ))}
          </select>
          <div className="header-actions">
            <span className="last-update">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" fill="currentColor"/>
              </svg>
              {lastUpdate}
            </span>
            <button onClick={handleLogout} className="logout-btn-dashboard">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" fill="currentColor"/>
              </svg>
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      {stats && (
        <>
          <div className="stats-cards">
            <div className="card total">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-1 .05 1.16.84 2 1.87 2 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
              </svg>
              <h3>Total invités</h3>
              <p className="number">{stats.totals.totalInvites}</p>
            </div>
            <div className="card confirmed">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
              </svg>
              <h3>Confirmés</h3>
              <p className="number">{stats.totals.confirmedCount}</p>
              <span className="percent">{stats.totals.taux}%</span>
            </div>
            <div className="card pending">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" fill="currentColor"/>
              </svg>
              <h3>En attente</h3>
              <p className="number">{stats.totals.pendingCount}</p>
            </div>
          </div>

          <div className="filters">
            <button className={filter.statut === "all" ? "active" : ""} onClick={() => setFilter({ ...filter, statut: "all" })}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 6h16v2H4V6zm2-4h12v2H6V2zm16 8H2v12h20V10zm-2 10H4v-8h16v8z" fill="currentColor"/>
              </svg>
              Tous
            </button>
            <button className={filter.statut === "confirmed" ? "active" : ""} onClick={() => setFilter({ ...filter, statut: "confirmed" })}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
              </svg>
              Confirmés
            </button>
            <button className={filter.statut === "pending" ? "active" : ""} onClick={() => setFilter({ ...filter, statut: "pending" })}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
                <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" fill="none"/>
              </svg>
              En attente
            </button>
            <input
              type="text"
              placeholder="Rechercher un invité..."
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              className="search-input"
            />
          </div>

          <div className="invites-table-container" ref={tableContainerRef}>
            {loading ? (
              <div className="loading">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="currentColor" opacity="0.3"/>
                  <path d="M12 2v4c4.41 0 8 3.59 8 8h4c0-6.63-5.37-12-12-12z" fill="currentColor">
                    <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                  </path>
                </svg>
                Chargement...
              </div>
            ) : (
              <table className="invites-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Table</th>
                    <th>Couple</th>
                    <th>Statut</th>
                    <th>Boisson</th>
                    <th>Téléphone</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map(invite => (
                    <tr key={invite._id} className={invite.statut === "confirmed" ? "confirmed-row" : "pending-row"}>
                      <td>{invite.nom}</td>
                      <td>{invite.table || "-"}</td>
                      <td>
                        {invite.couple === "oui" ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-1 .05 1.16.84 2 1.87 2 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
                          </svg>
                        )}
                      </td>
                      <td className={invite.statut === "confirmed" ? "confirmed-status" : "pending-status"}>
                        {invite.statut === "confirmed" ? "Confirmé" : "En attente"}
                      </td>
                      <td>{invite.boissonPreferee || "-"}</td>
                      <td>{invite.telephone || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default OrganizerDashboard;